import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, Eye, Check } from "lucide-react";
import { rolesAPI, type Rol, type ModulePermission } from "@/lib/api";
import { availableModules } from "@/types/modules";
import { ROLE_MATRIX_ONLY_MODULES } from "./rolePermissionCatalog";
import { ListNewButton, ListNewLoading } from "@/components/ListNewButton";

interface Props {
  onEdit?: (item: Rol) => void;
  onNew?: () => void;
}

export function RolesListado({ onEdit, onNew }: Props) {
  const [currentPage, setCurrentPage] = useState(1);
  const [roles, setRoles] = useState<Rol[]>([]);
  const [loading, setLoading] = useState(true);
  const itemsPerPage = 10;

  // Fetch roles on mount
  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const data = await rolesAPI.list();
        setRoles(data);
      } catch (error) {
        console.error("Error loading roles:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchRoles();
  }, []);

  const totalPages = Math.ceil(roles.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentRoles = roles.slice(startIndex, endIndex);

  const getModuleDisplay = (permissions: ModulePermission[]): React.ReactElement => {
    return (
      <div className="flex flex-wrap gap-1">
        {permissions.map((perm) => {
          const module =
            availableModules.find((m) => m.id === perm.moduleId) ||
            ROLE_MATRIX_ONLY_MODULES.find((m) => m.id === perm.moduleId);
          if (!module) return null;

          return (
            <div
              key={perm.moduleId}
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs ${perm.permission === 'read'
                ? 'bg-blue-100 text-blue-700 border border-blue-300'
                : 'bg-green-100 text-green-700 border border-green-300'
                }`}
              title={`${module.name} - ${perm.permission === 'read' ? 'Solo lectura' : 'Acceso completo'}`}
            >
              {perm.permission === 'read' ? (
                <Eye className="w-3 h-3" />
              ) : (
                <Check className="w-3 h-3" />
              )}
              <span>{module.name}</span>
            </div>
          );
        })}
      </div>
    );
  };

  if (loading) {
    return <ListNewLoading onNew={onNew} message="Cargando roles..." />;
  }

  return (
    <div className="space-y-4 mt-4">
      {onNew ? (
        <div className="flex justify-end">
          <ListNewButton onClick={onNew} />
        </div>
      ) : null}
      <div className="rounded-lg border border-border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50">
              <TableHead className="font-semibold">Código</TableHead>
              <TableHead className="font-semibold">Nombre</TableHead>
              <TableHead className="font-semibold">Descripción</TableHead>
              <TableHead className="font-semibold">Módulos y Permisos</TableHead>
              <TableHead className="font-semibold text-center">Total</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {currentRoles.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                  No hay roles registrados
                </TableCell>
              </TableRow>
            ) : (
              currentRoles.map((rol) => (
                <TableRow
                  key={rol.id ?? rol.codigo ?? rol.nombre}
                  className="hover:bg-muted/30 cursor-pointer"
                  onClick={() => onEdit?.(rol)}
                >
                  <TableCell className="font-medium">{rol.codigo}</TableCell>
                  <TableCell>{rol.nombre}</TableCell>
                  <TableCell className="max-w-xs">
                    <div className="line-clamp-2 text-sm text-muted-foreground">
                      {rol.descripcion}
                    </div>
                  </TableCell>
                  <TableCell className="max-w-2xl">
                    {getModuleDisplay(rol.modulosPermitidos as ModulePermission[])}
                  </TableCell>
                  <TableCell className="text-center">
                    <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary font-semibold text-sm">
                      {rol.modulosPermitidos?.length || 0}
                    </span>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            Mostrando {startIndex + 1}-{Math.min(endIndex, roles.length)} de {roles.length} roles
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <div className="flex items-center gap-1">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <Button
                  key={page}
                  variant={currentPage === page ? "default" : "outline"}
                  size="sm"
                  onClick={() => setCurrentPage(page)}
                  className="w-8 h-8 p-0"
                >
                  {page}
                </Button>
              ))}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}